import { memo, ReactNode, useCallback, useMemo, useState } from 'react'

import { ContactAvatar, getContactDisplayName, IContact } from 'clickNgo/entities/contact'
import {
  AbsolutePopup,
  PopPaper,
  TPopupHorizontal,
  TPopupVertical,
  TVisibilityTrigger,
} from 'core-components/library/popup'

import styles from './ContactDetailsPopup.module.scss'

type ContactDetailsPopupProps = {
  contact: IContact
  roles?: string[]
  detailItems?: string[]
  location?: string
  children: ReactNode
}

type PhoneInfo = {
  label: string
  number: string
  code: string
}

type InfoItem = {
  label: string
  value: string
  icon: string
}

const formatPhoneNumber = (code: string, number: string): string => {
  if (!number) return ''
  return code ? `${code} ${number}` : number
}

export const ContactDetailsPopup = memo<ContactDetailsPopupProps>(
  ({
    contact,
    roles: rolesProp,
    detailItems: detailItemsProp,
    location: locationProp,
    children,
  }) => {
    const [isOpen, setIsOpen] = useState(false)
    const displayName = getContactDisplayName(contact)

    const { roles, location, detailItems } = useMemo(() => {
      if (rolesProp !== undefined && detailItemsProp !== undefined && locationProp !== undefined) {
        return { roles: rolesProp, location: locationProp, detailItems: detailItemsProp }
      }

      const contactRoles =
        rolesProp ?? (contact.hpRoles?.length ? contact.hpRoles : contact.primaryJobRole || [])

      const contactLocation =
        locationProp ??
        [contact.city, contact.country || contact.countryOfBusiness].filter(Boolean).join(', ')

      const items =
        detailItemsProp ??
        ([
          contact.market || contact.region,
          contact.organization,
          contactLocation || undefined,
        ].filter(Boolean) as string[])

      return {
        roles: contactRoles,
        location: contactLocation,
        detailItems: items,
      }
    }, [contact, rolesProp, detailItemsProp, locationProp])

    const phoneNumbers = useMemo<PhoneInfo[]>(() => {
      const phones: PhoneInfo[] = []

      if (contact.mainPhoneNumber) {
        phones.push({
          label: 'Main',
          number: contact.mainPhoneNumber,
          code: contact.mainPhoneCode || '',
        })
      }

      if (contact.mobilePhoneNumber) {
        phones.push({
          label: 'Mobile',
          number: contact.mobilePhoneNumber,
          code: contact.mobilePhoneCode || '',
        })
      }

      if (contact.workPhoneNumber) {
        phones.push({
          label: 'Work',
          number: contact.workPhoneNumber,
          code: contact.workPhoneCode || '',
        })
      }

      return phones
    }, [contact])

    const additionalInfo = useMemo<InfoItem[]>(() => {
      const items: InfoItem[] = []

      if (contact.contactId) {
        items.push({ label: 'Contact ID', value: contact.contactId, icon: 'kbase kbase-user' })
      }

      if (contact.learnerId) {
        items.push({
          label: 'Learner ID',
          value: contact.learnerId,
          icon: 'kbase kbase-graduation',
        })
      }

      if (contact.locationId) {
        items.push({ label: 'Location ID', value: contact.locationId, icon: 'kbase kbase-pushpin' })
      }

      if (contact.partnerId) {
        items.push({ label: 'Partner ID', value: contact.partnerId, icon: 'kbase kbase-briefcase' })
      }

      if (contact.countryOfBusiness) {
        items.push({
          label: 'Country of Business',
          value: contact.countryOfBusiness,
          icon: 'kbase kbase-globe',
        })
      }

      if (contact.preferredLanguage) {
        items.push({
          label: 'Preferred Language',
          value: contact.preferredLanguage,
          icon: 'kbase kbase-chat',
        })
      }

      if (contact.primaryJobRole?.length) {
        items.push({
          label: 'Job Function',
          value: contact.primaryJobRole.join(', '),
          icon: 'kbase kbase-briefcase',
        })
      }

      return items
    }, [contact])

    const contactFlags = useMemo(() => {
      const flags: { label: string; value: boolean; icon: string }[] = []

      if (contact.isNeverCall) {
        flags.push({ label: 'Never Call', value: true, icon: 'kbase kbase-phone-off' })
      }

      if (contact.isNeverEmail) {
        flags.push({ label: 'Never Email', value: true, icon: 'kbase kbase-mail-off' })
      }

      return flags
    }, [contact])

    const handlePopupVisibility = useCallback((visible: boolean) => {
      setIsOpen(visible)
      return visible
    }, [])

    return (
      <AbsolutePopup
        popupClassName={styles.popupWrapper}
        verticalStartPosition={TPopupVertical.Center}
        horizontalStartPosition={TPopupHorizontal.Left}
        horizontalOffset={24}
        visibilityTrigger={TVisibilityTrigger.Hover}
        visibilityCallback={handlePopupVisibility}
        popupContent={({ action }) =>
          isOpen ? (
            <PopPaper className={styles.popover} shadowPosition="bottom">
              <div className={styles.topbar}>
                <div className={styles.title}>Contact details</div>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={action.close}
                  aria-label="Close"
                >
                  <i className="kbase kbase-close" />
                </button>
              </div>
              <div className={styles.header}>
                <ContactAvatar
                  photo={contact.photo}
                  firstName={contact.firstName}
                  lastName={contact.lastName}
                  email={contact.email}
                  size="lg"
                  className={styles.avatar}
                />
                <div className={styles.headerInfo}>
                  <div className={styles.name}>{displayName}</div>
                  <div className={styles.email}>{contact.email}</div>
                </div>
              </div>
              {/* Contact Flags */}
              {contactFlags.length > 0 && (
                <div className={styles.flags}>
                  {contactFlags.map((flag) => (
                    <span key={flag.label} className={styles.flag}>
                      <i className={flag.icon} />
                      <span>{flag.label}</span>
                    </span>
                  ))}
                </div>
              )}
              <div className={styles.divider} />
              <div className={styles.body}>
                {/* Roles */}
                <div className={styles.sectionTitle}>Roles</div>
                <div className={styles.roles}>
                  {roles.length ? (
                    roles.map((role) => (
                      <span key={`${contact.email}-${role}`} className={styles.role}>
                        {role}
                      </span>
                    ))
                  ) : (
                    <span className={styles.empty}>No roles</span>
                  )}
                </div>
                <div className={styles.divider} />
                {/* Phone Numbers */}
                {phoneNumbers.length > 0 && (
                  <>
                    <div className={styles.sectionTitle}>Phone numbers</div>
                    <div className={styles.list}>
                      {phoneNumbers.map((phone) => (
                        <div key={`${contact.email}-phone-${phone.label}`} className={styles.item}>
                          <span className={styles.itemIcon}>
                            <i className="kbase kbase-omni-mobile" />
                          </span>
                          <span>
                            <span className={styles.itemLabel}>{phone.label}:</span>{' '}
                            {formatPhoneNumber(phone.code, phone.number)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Location Details */}
                <div className={styles.divider} />
                <div className={styles.sectionTitle}>Location</div>
                <div className={styles.list}>
                  {detailItems.map((item) => (
                    <div key={`${contact.email}-${item}`} className={styles.item}>
                      <span className={styles.itemIcon}>
                        {item === contact.organization ? (
                          <i className="kbase kbase-home" />
                        ) : item === location ? (
                          <i className="kbase kbase-pushpin" />
                        ) : (
                          <i className="kbase kbase-globe" />
                        )}
                      </span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                {/* Additional Info */}
                {additionalInfo.length > 0 && (
                  <>
                    <div className={styles.divider} />
                    <div className={styles.sectionTitle}>Additional info</div>
                    <div className={styles.list}>
                      {additionalInfo.map((info) => (
                        <div key={`${contact.email}-info-${info.label}`} className={styles.item}>
                          <span className={styles.itemIcon}>
                            <i className={info.icon} />
                          </span>
                          <span>
                            <span className={styles.itemLabel}>{info.label}:</span> {info.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </PopPaper>
          ) : null
        }
      >
        {children}
      </AbsolutePopup>
    )
  }
)

ContactDetailsPopup.displayName = 'ContactDetailsPopup'
